import { DomSanitizer } from '@angular/platform-browser';
import { Component, Input, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef,
  Renderer2, forwardRef, HostBinding, AfterViewInit, OnDestroy } from '@angular/core';
import { BASE_COMPONENT, WizBaseComponent } from '../../core/wiz-base-component';
import { first, Observable } from 'rxjs';
import { COMPONENTRESOURCES } from '../../../../../assets/componentresources';
import { IWIZ_COMPONENT} from '../../../interfaces/iwiz-component';
import { WizFileUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../events/wiz-event';
import { WizEventManager } from '../../../events/wiz-event.manager';


@Component({
  selector: 'wiz-image',
  template: `<img [tabIndex]="wizTabIndex" [src]=source draggable="false"/>`,
  styleUrls: ['./wiz-image.scss'],
  providers: [
    {provide: BASE_COMPONENT, useExisting: forwardRef(() => WizImage)},
    {provide: WizBaseComponent, useExisting: forwardRef(() => WizImage)},
    {provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizImage)}
  ],
  changeDetection: ChangeDetectionStrategy.OnPush // pas de listeneur permanent

})
export class WizImage extends WizBaseComponent implements AfterViewInit, OnDestroy {



  /** Constructor with translation engine
   * @param cdref: ChangeDetectorRef
   * @param elementRef: the dom of the component
   * @param renderer: object that will allow the interaction between the component and the DOM
   */
  constructor(public cdref: ChangeDetectorRef,
              public elementRef: ElementRef,
              public renderer: Renderer2,
              private sanitizer: DomSanitizer) {
      super(cdref, elementRef, renderer);
  }

  /** gestion de la visibilité  : pas visible non plus si source = null*/
  @HostBinding('class.invisible-class') get visibility() {
    return this.visible === false || !this.source;
  }

  @Input()
  public blobid: string = null; // référence du blobID pour passer au zoom

  @Input()
  public disabledClassName: string = 'disabled-class'; // class css pour skin "disabled"

  @Input()
  public buttonModeClassName: string = 'buttonmode-class'; // class css pour mode bouton

  ngAfterViewInit() {
    super.ngAfterViewInit();

    WizEventManager.addListener(this, this, 'click', this.onClick, { capture: true });
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    WizEventManager.removeListener(this, this, 'click');
  }


  protected onClick(pEvent: WizEvent): void {
    if (this.wizDisabled) {
      pEvent.preventDefault();
      pEvent.stopImmediatePropagation();
      pEvent.stopPropagation();
    }
  }

  private _source: string; // URL de la source
  @Input()
  public set source(v: string) {
    this.setSource(v);
  }
  public get source(): string {
    return this.getSource();
  }
  /**
   * methodes getter/setter surchargeables de la source de l'image
   */
  protected setSource(value: string): void {
    this._source = value;
    this.cdref.markForCheck();
  }
  protected getSource(): string {
    return this._source;
  }

  /**
   * alimentation asynchrone (médias remotes du storage)
   */
  @Input()
  public set sourceObservable(obs: Observable<string>) {
    if(obs) {
        obs.pipe(first()).subscribe(item => {
        const bin: any = WizFileUtils.imageFromBinaryString(item, COMPONENTRESOURCES.COMMON_UPLOAD);
        this.setSource(!!item ? this.sanitizer.bypassSecurityTrustResourceUrl(bin) : bin);
      });
    } else { // icon par défaut
      this.setSource(COMPONENTRESOURCES.COMMON_UPLOAD);
    }
  }


  private _useHandCursor: boolean = false; // mode bouton
  @Input()
  public set useHandCursor(v: boolean) {
    this.setUseHandCursor(v);
  }
  public get useHandCursor(): boolean {
    return this.getUseHandCursor();
  }
  /**
   * methodes getter/setter surchargeables de UseHandCursor
   */
  protected setUseHandCursor(value: boolean): void {
    this._useHandCursor = value;
    this.checkStyle(); // gestion de la résultante de styles
    this.cdref.markForCheck();
  }
  protected getUseHandCursor(): boolean {
    return this._useHandCursor;
  }

  /** application du style effectif (disabled vs pointer) */
  public checkStyle() {
    if (this.wizDisabled) { // inactif
      this.renderer.addClass(this.elementRef.nativeElement, this.disabledClassName);
      this.renderer.removeClass(this.elementRef.nativeElement, this.buttonModeClassName);
    } else { // actif
      this.renderer.removeClass(this.elementRef.nativeElement, this.disabledClassName);
      if (this._useHandCursor) { // et buttonMode
        this.renderer.addClass(this.elementRef.nativeElement, this.buttonModeClassName);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, this.buttonModeClassName);
      }
    }
  }

  /** donne le focus à l'input depuis FocusManager */
  public wizFocus(): void {
    this.elementRef.nativeElement.focus();
    this.wizGetFocus();
  }
}
